import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import MainContainer from "../components/MainContainer";
import Hero from "../components/Hero/Hero";
import InfoStrip from "../components/InfoStrip/InfoStrip";
import Marquee from "../components/Marquee/Marquee";
import KaizenSection from "../components/KaizenSection/KaizenSection";
import CategoryRows from "../components/CategoryRows/CategoryRows";
import ProductCard from "../components/utils/ProductCard";
import SkeletonProductCard from "../components/utils/SkeletonProductCard";
import Reveal from "../components/utils/Reveal";
import {
  selectCategories,
  selectProducts,
} from "../store/slices/products/slice";
import { getProducts } from "../store/slices/products/asyncThunks";

const HomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const products = useSelector(selectProducts);
  const categories = useSelector(selectCategories);

  useEffect(() => {
    dispatch(getProducts())
      .unwrap()
      .then(() => {})
      .catch((err) => console.log(err.message));
  }, []);

  const novo = useMemo(() => {
    const list = [...products.data];
    list.sort(
      (a, b) =>
        new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0) ||
        (b?.id || 0) - (a?.id || 0),
    );
    return list.slice(0, 4);
  }, [products]);

  const akcija = useMemo(
    () =>
      products.data
        .filter(
          (p) =>
            p?.salePrice !== undefined &&
            p?.salePrice !== null &&
            p?.salePrice !== "",
        )
        .slice(0, 4),
    [products],
  );

  const goShop = (kategorija) => {
    navigate("/prodavnica", { state: { kategorija } });
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  return (
    <MainContainer offPadding hideLogo>
      <Hero />
      <Reveal y={20}>
        <InfoStrip />
      </Reveal>
      <Marquee />
      <KaizenSection />

      <HomeSection>
        <Reveal>
          <HeadRow>
            <div>
              <SLabel>001 / Novo u ponudi</SLabel>
              <H2>
                Poslednji <i>drop</i>
              </H2>
            </div>
            <ViewAll onClick={() => goShop("Sve")}>Prikaži sve →</ViewAll>
          </HeadRow>
        </Reveal>

        <Reveal delay={0.12}>
          <Grid>
            {products.loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <SkeletonProductCard key={i} />
                ))
              : novo.map((p) => <ProductCard key={p.id} product={p} shop />)}
          </Grid>
        </Reveal>
      </HomeSection>

      <CategoryRows categories={categories.data} />

      {akcija.length > 0 && (
        <AkcijaSection>
          <HomeSection as="div">
            <Reveal>
              <HeadRow>
                <div>
                  <SLabel>003 / Akcija</SLabel>
                  <H2>
                    Sniženo — <i>dok traje</i>
                  </H2>
                </div>
                <ViewAll onClick={() => goShop("Akcija")}>
                  Sve na akciji →
                </ViewAll>
              </HeadRow>
            </Reveal>

            <Reveal delay={0.12}>
              <Grid>
                {akcija.map((p) => (
                  <ProductCard key={p.id} product={p} shop />
                ))}
              </Grid>
            </Reveal>
          </HomeSection>
        </AkcijaSection>
      )}
    </MainContainer>
  );
};

export default HomePage;

const HomeSection = styled.section`
  width: 100%;
  max-width: 132rem;
  margin: 0 auto;
  padding: 7rem clamp(1.6rem, 4vw, 4.8rem) 3rem;
`;

const HeadRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 2rem;
  flex-wrap: wrap;
  margin-bottom: 3.4rem;
`;

const SLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  font-size: 1.1rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--red, #fe5f55);
  font-family: "Poppins-Medium";
  margin-bottom: 1rem;

  &::before {
    content: "";
    width: 3.4rem;
    height: 1px;
    background: var(--red, #fe5f55);
  }
`;

const H2 = styled.h2`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: clamp(2.6rem, 4.6vw, 4rem);
  line-height: 1;
  letter-spacing: 0.01em;
  color: var(--text, #f4f2ee);

  i {
    font-style: normal;
    color: var(--red, #fe5f55);
  }
`;

const ViewAll = styled.button`
  font-size: 1.2rem;
  font-family: "Poppins-Medium";
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--muted, #8e8c94);
  background: none;
  border: none;
  border-bottom: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  padding-bottom: 0.4rem;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    color: var(--red, #fe5f55);
    border-color: var(--red, #fe5f55);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.8rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const AkcijaSection = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  background: var(--bg1, #101013);
  border-top: 1px solid rgba(254, 95, 85, 0.22);
  border-bottom: 1px solid rgba(254, 95, 85, 0.22);

  &::before {
    content: "";
    position: absolute;
    top: -22rem;
    left: -16rem;
    width: 62rem;
    height: 62rem;
    background: radial-gradient(
      circle,
      rgba(254, 95, 85, 0.13),
      transparent 65%
    );
    pointer-events: none;
  }

  &::after {
    content: "善";
    position: absolute;
    right: -3rem;
    bottom: -7rem;
    font-family: var(--font-kanji);
    font-size: 38rem;
    line-height: 1;
    color: rgba(254, 95, 85, 0.045);
    pointer-events: none;
  }
`;
